import React, { useState, useEffect } from 'react';
import Select, { components } from 'react-select';
import { getListaMensilita, getPacchettiSms, getStatoServizio, downloadFattura, iniziaPagamentoPaypal } from '../api';
import { showError, fetchWithRetry } from '../utils';
import Loader from './layout/Loader';
import AccordionItem from './layout/AccordionItem';
import Page from './layout/Page';
import Card from './layout/Card';
import useConfirm from '../hooks/useConfirm';
import {ReactComponent as BuyIcon} from '../assets/icons/buy.svg';
import {ReactComponent as ExportPdfIcon} from '../assets/icons/export-pdf.svg';
import {ReactComponent as LoadingIcon} from '../assets/icons/spinner.svg';
import { ReactComponent as CalendarIcon } from '../assets/icons/calendar-icon-cal.svg';

// definisci il DropdownIndicator personalizzato
const DropdownIndicator = props => (
  <components.DropdownIndicator {...props}>
    <CalendarIcon />
  </components.DropdownIndicator>
);


const StatoServizio = () => {
  const [mensilita, setMensilita] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [statoData, setStatoData] = useState(null);
  const [pacchettiSms, setPacchettiSms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingFattura, setLoadingFattura] = useState(null);
  const [loadingPayment, setLoadingPayment] = useState(null);
  const [loadingPackage, setLoadingPackage] = useState(null);
  const confirm = useConfirm();

  // Carica dati iniziali
  useEffect(() => {
    let ignore = false;
    const loadInitialData = async () => {
      setLoading(true);
      
      // Carica mensilità e pacchetti SMS
      const [mensilityData, pacchettiData] = await Promise.all([
        getListaMensilita(),
        getPacchettiSms()
      ]);

      if (ignore) return; // Ignora se il componente è stato smontato

      // Elabora mensilità
      if (mensilityData) {
        const options = Object.entries(mensilityData).map(([key, value]) => ({
          value: key,
          label: formatMonthLabel(key)
        }));
        setMensilita(options);
        
        // Seleziona l'ultima mensilità per default
        if (options.length > 0) {
          const lastMonth = options[options.length - 1];
          setSelectedMonth(lastMonth);
          loadStatoData(lastMonth.value);
        }
      }

      // Elabora pacchetti SMS
      if (pacchettiData) {
        setPacchettiSms(pacchettiData);
      }
    };

    fetchWithRetry(loadInitialData, ignore)
    .catch(err => {
      if (!ignore) {
        showError('Errore nel caricamento dati iniziali: ' + err.message);
      }
    })
    .finally(() => {
      if (!ignore) {
        setLoading(false);
      }
    });

    return () => {
        ignore = true; // Imposta ignore a true quando il componente viene smontato
    }
  }, []);

  // Carica stato servizio per un mese specifico
  const loadStatoData = async (month) => {
    try {
      const data = await getStatoServizio(month);
      setStatoData(data);
    } catch (error) {
      showError('Errore nel caricamento stato servizio: ' + error.message);
    }
  };

  // Gestisce cambio mese
  const handleMonthChange = (selectedOption) => {
    setSelectedMonth(selectedOption);
    if (selectedOption) {
      loadStatoData(selectedOption.value);
    }
  };

  // Formatta label del mese (es: "202501" -> "Gennaio 2025")
  const formatMonthLabel = (monthStr) => {
    const year = monthStr.substring(0, 4);
    const month = parseInt(monthStr.substring(4, 6));
    const monthNames = [
      'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
      'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
    ];
    return `${monthNames[month - 1]} ${year}`;
  };

  // Formatta durata da secondi a formato leggibile
  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours}h ${minutes}m ${secs}s`;
  };

  // Formatta data
  const formatDate = (dateStr) => {
    const date = new Date(dateStr.substring(0, 8).replace(/(\d{4})(\d{2})(\d{2})/, '$1-$2-$3'));
    return date.toLocaleDateString('it-IT');
  };

  // Download fattura
  const handleDownloadFattura = async (fatturaId, idfile) => {
    try {
      setLoadingFattura(fatturaId);
      const data = await downloadFattura(idfile);
      
      if (data) {
        // Converte base64 in blob e scarica
        const byteCharacters = atob(data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `fattura_${fatturaId}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      showError('Errore nel download fattura: ' + error.message);
    } finally {
      setLoadingFattura(null);
    }
  };

   const handlePayPalPayment = async (fatturaId, importo, num) => {
    const ok = await confirm({
      message: `Vuoi procedere con il pagamento della fattura ${num} di € ${importo.toFixed(2)}?`,
    });
    if (!ok) return;
    setLoadingPayment(fatturaId);
    try {
      const data = await iniziaPagamentoPaypal(fatturaId);
      if (data?.paypal_url) window.location.href = data.paypal_url;
    } catch (error) {
      showError('Errore durante il pagamento PayPal: ' + error.message);
      setLoadingPayment(null);
    }
  };

  const handlePurchasePackage = async (pacchettoId, value, euro) => {
    const ok = await confirm({
      message: `Vuoi procedere con l'acquisto di un pacchetto da ${value} per € ${euro.toFixed(2)}?`,
    });
    if (!ok) return;
    setLoadingPackage(pacchettoId);
    try {
      const data = await iniziaPagamentoPaypal(null, pacchettoId);
      if (data?.paypal_url) window.location.href = data.paypal_url;
    } catch (error) {
      showError('Errore durante acquisto pacchetto: ' + error.message);
      setLoadingPackage(null);
    }
  };

  if (loading) {
    return <Page title={"Stato del Servizio"} >
                    <Loader />
           </Page>;  
  }

  return (
    <Page title={"Stato del Servizio"} >
      <Card content={
        <div className="stato-servizio">
          <AccordionItem title={"Stato del Servizio"}>
              <div className="stato-servizio__month-selector">
                <label className="stato-servizio__month-label">Seleziona il mese</label>
                <Select
                  value={selectedMonth}
                  onChange={handleMonthChange}
                  options={mensilita}
                  placeholder="Seleziona mese..."
                  className="stato-servizio__month-select"
                  classNamePrefix="stato-servizio__month-select"
                  menuPosition="fixed"                              // ← posizione fissa
                  menuPortalTarget={document.body}                  // ← render in portal sul body
                  styles={{
                    menuPortal: base => ({ 
                      ...base, 
                      zIndex: 9999                                   // ← z-index alto 
                    })
                  }}
                  components={{ DropdownIndicator }}
                />
              </div>
          </AccordionItem>
          {/* Card Stato Servizio */}
          {statoData?.stato_servizio && (
            <AccordionItem title={ "Report Mensile " + formatMonthLabel(statoData.stato_servizio.month)}>
                <div className="stato-servizio__card">                  
                  <div className="stato-servizio__stats-grid">
                    <div className="stato-servizio__stat-item">
                      <span className="stato-servizio__stat-label">SMS Ricevuti</span>
                      <span className="stato-servizio__stat-value">{statoData.stato_servizio.numsms}</span>
                    </div>
                    
                    <div className="stato-servizio__stat-item">
                      <span className="stato-servizio__stat-label">Email Ricevute</span>
                      <span className="stato-servizio__stat-value">{statoData.stato_servizio.nummail}</span>
                    </div>
                    
                    <div className="stato-servizio__stat-item">
                      <span className="stato-servizio__stat-label">Chiamate Ricevute</span>
                      <span className="stato-servizio__stat-value">{statoData.stato_servizio.numcallrisp}</span>
                    </div>
                    
                    <div className="stato-servizio__stat-item">
                      <span className="stato-servizio__stat-label">Durata Chiamate In</span>
                      <span className="stato-servizio__stat-value">{formatDuration(statoData.stato_servizio.duratasecfatt)}</span>
                    </div>
                    
                    <div className="stato-servizio__stat-item">
                      <span className="stato-servizio__stat-label">Chiamate Effettuate</span>
                      <span className="stato-servizio__stat-value">{statoData.stato_servizio.numcallout}</span>
                    </div>
                    
                    <div className="stato-servizio__stat-item">
                      <span className="stato-servizio__stat-label">Durata Chiamate Out</span>
                      <span className="stato-servizio__stat-value">{formatDuration(statoData.stato_servizio.duratasecoutfatt)}</span>
                    </div>
                    
                    <div className="stato-servizio__stat-item">
                      <span className="stato-servizio__stat-label">TODO</span>
                      <span className="stato-servizio__stat-value">{statoData.stato_servizio.numtodo}</span>
                    </div>
                    
                    <div className="stato-servizio__stat-item">
                      <span className="stato-servizio__stat-label">SMS Inviati</span>
                      <span className="stato-servizio__stat-value">{statoData.stato_servizio.numsmsout}</span>
                    </div>
                    
                    <div className="stato-servizio__stat-item">
                      <span className="stato-servizio__stat-label">SMS Calls</span>
                      <span className="stato-servizio__stat-value">{statoData.stato_servizio.smscalls}</span>
                    </div>
                    
                    <div className="stato-servizio__stat-item">
                      <span className="stato-servizio__stat-label">Lista Attesa</span>
                      <span className="stato-servizio__stat-value">{statoData.stato_servizio.listaattesa}</span>
                    </div>
                  </div>
                </div>
            </AccordionItem>
          )}
          

          {/* Banner Pacchetti SMS */}
          {pacchettiSms.length > 0 && (
            <AccordionItem title={"Acquista Pacchetti Minuti"}>
              <div className="stato-servizio__banner">
                <div className="stato-servizio__packages">
                  {pacchettiSms.map((pacchetto) => (
                    <div key={pacchetto.id} className="stato-servizio__package-card">
                      <div className="stato-servizio__package-value">{pacchetto.value}</div>
                      <div className="stato-servizio__package-price">€ {pacchetto.euro.toFixed(2)}</div>
                      <button 
                        className="icon-button stato-servizio__package-btn"
                        onClick={() => handlePurchasePackage(pacchetto.id, pacchetto.value, pacchetto.euro)}
                        disabled={loadingPackage === pacchetto.id}
                      >
                        {loadingPackage === pacchetto.id ? <LoadingIcon className="icon-loading"/> : <BuyIcon />}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </AccordionItem>
          )}

          {/* Tabella Fatture */}
          {statoData?.fatture && (
            <AccordionItem title={"Ultime Fatture"}>
              <div className="stato-servizio__invoices">
                <div className="stato-servizio__table-wrapper">
                  <table className="stato-servizio__table">
                    <thead className="stato-servizio__table-head">
                      <tr>
                        <th>Numero</th>
                        <th>Data Emissione</th>
                        <th>Data Scadenza</th>
                        <th>Importo</th>
                        <th>Stato</th>
                        <th>Azioni</th>
                      </tr>
                    </thead>
                    <tbody className="stato-servizio__table-body">
                      {statoData.fatture.map((fattura) => (
                        <tr key={fattura.id} className="stato-servizio__table-row">
                          <td className="stato-servizio__table-cell">{fattura.num}</td>
                          <td className="stato-servizio__table-cell">{formatDate(fattura.dataemissione)}</td>
                          <td className="stato-servizio__table-cell">{formatDate(fattura.datascadenza)}</td>
                          <td className="stato-servizio__table-cell">{fattura.importo ? "€ " + fattura.importo?.toFixed(2) : 'N/A'}</td>
                          <td className="stato-servizio__table-cell">
                            <span className={`stato-servizio__status ${fattura.ispagata ? 'stato-servizio__status--paid' : 'stato-servizio__status--unpaid'}`}>
                              {fattura.ispagata ? 'Pagata' : 'Da Pagare'}
                            </span>
                          </td>
                          <td className="stato-servizio__table-cell">
                            <div className="stato-servizio__actions">
                              {fattura.ids_pdf.length > 0 && (
                                <button
                                  onClick={() => handleDownloadFattura(fattura.id, fattura.ids_pdf[0])}
                                  disabled={loadingFattura === fattura.id}
                                  className="icon-button stato-servizio__download-btn"
                                >
                                  {loadingFattura === fattura.id ? <LoadingIcon className='icon-loading'/> : <ExportPdfIcon />}
                                </button>
                              )}
                              
                              {!fattura.ispagata && fattura.importo && (
                                <button
                                  onClick={() => handlePayPalPayment(fattura.id, fattura.importo, fattura.num)}
                                  disabled={loadingPayment === fattura.id}
                                  className="icon-button stato-servizio__paypal-btn"
                                >
                                  {loadingPayment === fattura.id ? <LoadingIcon className="icon-loading"/> : <BuyIcon />}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </AccordionItem>
          )}
        </div>
        }/>
    </Page>
  );
};

export default StatoServizio;